import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY } from './contactInfo';

export const privacyPolicy = {
  title: 'Privacy Policy',
  updated: 'June 2026',
  sections: [
    {
      heading: 'Introduction',
      body: 'Lumora ("we", "our", or "us") respects your privacy. This policy explains how we collect, use, and protect your personal information when you visit lumora.pk, browse our handcrafted wooden lamp collection, or place an order with us.',
    },
    {
      heading: 'Information We Collect',
      body: 'We may collect your name, email address, phone number, and delivery address when you create an order, contact us, or subscribe to updates. We also collect basic technical data such as browser type and pages visited to improve your shopping experience.',
    },
    {
      heading: 'How We Use Your Information',
      body: 'Your information is used to process orders, arrange delivery of your lamps, respond to enquiries, send order updates, and improve our products and website. We do not sell your personal data to third parties.',
    },
    {
      heading: 'Payment & Order Data',
      body: 'Order details and payment method preferences are stored securely to fulfil your purchase. If you choose cash on delivery or bank transfer, we only retain the information necessary to complete and verify your transaction.',
    },
    {
      heading: 'Cookies',
      body: 'Our website may use cookies and similar technologies to remember your preferences, keep items in your cart, and understand how visitors use our store. You can disable cookies in your browser settings, though some features may not work as intended.',
    },
    {
      heading: 'Data Security',
      body: 'We take reasonable technical and organisational measures to protect your information. While no online service can guarantee absolute security, we work to keep your data safe and only retain it for as long as needed.',
    },
    {
      heading: 'Your Rights',
      body: `You may request access to, correction of, or deletion of your personal information by contacting us at ${CONTACT_EMAIL}. We will respond to legitimate requests within a reasonable timeframe.`,
    },
    {
      heading: 'Contact Us',
      body: `For any privacy-related questions, email ${CONTACT_EMAIL} or call ${CONTACT_PHONE_DISPLAY}.`,
    },
  ],
};

export const termsOfService = {
  title: 'Terms of Service',
  updated: 'June 2026',
  sections: [
    {
      heading: 'Agreement to Terms',
      body: 'By accessing lumora.pk or purchasing from Lumora, you agree to these Terms of Service. If you do not agree, please do not use our website or services.',
    },
    {
      heading: 'Our Products',
      body: 'Lumora offers handcrafted wooden lamps and related home décor lighting. Because each piece involves natural wood and artisan finishing, slight variations in grain, tone, and texture are normal and part of the product\'s character — not defects.',
    },
    {
      heading: 'Orders & Availability',
      body: 'All orders are subject to acceptance and stock availability. We reserve the right to refuse or cancel an order if an item is unavailable, if pricing was displayed in error, or if we suspect fraudulent activity.',
    },
    {
      heading: 'Pricing & Payment',
      body: 'Prices are listed in Pakistani Rupees (PKR) unless stated otherwise. We accept cash on delivery and bank transfer as shown at checkout. Payment must be completed according to the method selected before or upon delivery, as applicable.',
    },
    {
      heading: 'Shipping & Delivery',
      body: 'We deliver across Pakistan. Estimated delivery times are provided at checkout but may vary due to location, courier schedules, or unforeseen circumstances. Risk of loss passes to you upon successful delivery to the address provided.',
    },
    {
      heading: 'Returns & Refunds',
      body: 'If your lamp arrives damaged or significantly different from its description, contact us within 7 days of delivery with photos of the issue. Approved returns may be repaired, replaced, or refunded at our discretion. Custom or clearly used items may not be eligible for return.',
    },
    {
      heading: 'Intellectual Property',
      body: 'All Lumora branding, product photography, designs, and website content are owned by Lumora and may not be copied, reproduced, or used without our written permission.',
    },
    {
      heading: 'Limitation of Liability',
      body: 'Lumora is not liable for indirect, incidental, or consequential damages arising from the use of our products or website. Our total liability for any claim relating to an order shall not exceed the amount you paid for that order.',
    },
    {
      heading: 'Governing Law',
      body: 'These terms are governed by the laws of Pakistan. Any disputes shall be subject to the exclusive jurisdiction of the courts of Lahore, Pakistan.',
    },
    {
      heading: 'Contact',
      body: `Questions about these terms? Reach us at ${CONTACT_EMAIL} or ${CONTACT_PHONE_DISPLAY}.`,
    },
  ],
};

export const returnPolicy = {
  title: 'Return Policy',
  updated: 'August 2026',
  intro:
    'At Lumora, we take great care in crafting each product to ensure you receive a product that is beautiful, safe, and of the highest quality. However, if something goes wrong, we\'re here to help.',
  sections: [
    {
      heading: 'Return Conditions',
      body: 'You may request a return or refund only in the following cases:',
      bullets: [
        'The item is damaged or defective when delivered.',
        'The item is not the one you ordered.',
        'Returns and refunds are not accepted if you simply decide you no longer want the product.',
      ],
    },
    {
      heading: 'Non-Refundable Items',
      bullets: [
        'Used products that are not in their original condition.',
        'Custom or personalized product orders.',
        'Items reported after 3 days of delivery.',
      ],
    },
    {
      heading: 'Eligibility for Refund',
      bullets: [
        'Return is available within 3 days of delivery.',
        'To process your request, we may require clear photos or videos of the damaged, defective, or incorrect item.',
      ],
    },
    {
      heading: 'Return Process',
      bullets: [
        'Once your return request is approved, you will be asked to return the product.',
        'Return shipping costs will be covered by us for damaged, defective, or incorrect products.',
        'Return shipping costs will be covered by you if you ordered the wrong size or product.',
      ],
    },
    {
      heading: 'Refund Process',
      body: 'Refunds for approved returns due to customer reasons (such as ordering the wrong size or product) will be issued after a deduction of delivery cost from the total order amount. Refunds for damaged, defective, or incorrect products will be issued in full.',
    },
    {
      heading: 'How to Request a Refund',
      bullets: [
        `Contact us at ${CONTACT_EMAIL} or ${CONTACT_PHONE_DISPLAY} within 3 days of receiving your order.`,
        'Provide your order number, proof of purchase, and photos or videos of the issue.',
        'Our team will review your request and guide you through the return process.',
      ],
    },
  ],
};
